import * as Yup from 'yup';

import { DeliveryOption, IAdHocTokenReport } from '../../../lib/types/communication.types';
import {
  EMAIL_DELIVERY_OPTIONS,
  SMS_DELIVERY_OPTIONS
} from '../../../lib/helpers/communication.helpers';
import { indefiniteArticle } from '../../../lib/helpers/text.helpers';
import { stripHtml } from './compose-panel.helpers';
import { InsertTarget } from './compose-panel.insert';
import { messages } from './compose-panel.messages';
import { SMS_BODY_MAX } from '../../../lib/helpers/sms.helpers';

// Email options require subject + bodyHtml; SMS options require smsBody. The
// unused field stays unvalidated so switching deliveryOption keeps its content.
export const validationSchema = Yup.object().shape({
  // Title is the send's identifier in history and the player log — required for
  // every deliveryOption so a send never shows as "(no subject)".
  title: Yup.string().trim().required('Add a title'),
  deliveryOption: Yup.string().oneOf(Object.values(DeliveryOption)).required(),
  subject: Yup.string().when('deliveryOption', {
    is: (val: DeliveryOption) => EMAIL_DELIVERY_OPTIONS.includes(val),
    then: Yup.string().trim().required('Add a subject'),
    otherwise: Yup.string()
  }),
  bodyHtml: Yup.string().when('deliveryOption', {
    is: (val: DeliveryOption) => EMAIL_DELIVERY_OPTIONS.includes(val),
    then: Yup.string().test('not-empty', 'Write an email message', (val) => !!val && stripHtml(val).length > 0),
    otherwise: Yup.string()
  }),
  // smsBody is required only when the deliveryOption can send SMS; otherwise it
  // stays unvalidated so the user can author it and switch options freely.
  smsBody: Yup.string()
    .max(SMS_BODY_MAX, `Keep the SMS under ${SMS_BODY_MAX} characters`)
    .when('deliveryOption', {
      is: (val: DeliveryOption) => SMS_DELIVERY_OPTIONS.includes(val),
      then: Yup.string().trim().required('Write an SMS message'),
      otherwise: Yup.string()
    })
});

// Per-tier severity for the live per-field issue text: danger = a blocking hard
// error (unknown / split, red), warn = needs-a-default (orange).
export type TokenIssueTone = 'danger' | 'warn';

export interface IFieldTokenMessage {
  tone: TokenIssueTone;
  text: string;
}

interface IAuthoredContentValues {
  subject: string;
  bodyHtml: string;
  smsBody: string;
}

// Authored surfaces with the body pre-stripped to plaintext, for token matching.
interface IAuthoredSurfaces {
  subject: string;
  bodyText: string;
  smsBody: string;
}

export const formatTokenList = (keys: string[]): string => keys.map((key) => `{${key}}`).join(', ');

// Interpolation values for the token-tier templates — the token list plus the
// session term / article the copy needs.
export const buildTokenIssueValues = (keys: string[], sessionTerm: string): Record<string, string> => ({
  tokens: formatTokenList(keys),
  article: indefiniteArticle(sessionTerm),
  sessionTerm
});

// Fill a tier template's {placeholders} in a single pass — inserted values are
// never re-scanned (a token like {player.team} in the list stays inert).
const fillMessage = (template: string, values: Record<string, string>): string =>
  template.replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

// Matches a token in its delimited form ({path}, {path|fallback}, whitespace-
// tolerant) so 'player.team' isn't found inside '{player.teamName}'.
const tokenPresent = (text: string, path: string): boolean => {
  const escaped = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\{\\s*${escaped}\\s*[|}]`).test(text);
};

// Matches an occurrence with NO fallback ({path}, not {path|...}), so a token
// given a default — even an empty {path|} — no longer counts as needs-a-default.
const tokenPresentWithoutFallback = (text: string, path: string): boolean => {
  const escaped = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\{\\s*${escaped}\\s*\\}`).test(text);
};

// Locate which authored surface carries a reported token path, per the tier's
// matcher. bodyText is the pre-stripped plaintext (stripped once) so no re-parse.
// A reported path in no surface (a just-deleted token under a lagging live report)
// maps to nothing, so the live per-field text clears in step with the highlight.
const surfacesContaining = (
  path: string,
  surfaces: IAuthoredSurfaces,
  matcher: (text: string, path: string) => boolean
): InsertTarget[] => {
  const found: InsertTarget[] = [];
  if (matcher(surfaces.subject, path)) found.push('subject');
  if (matcher(surfaces.bodyText, path)) found.push('bodyHtml');
  if (matcher(surfaces.smsBody, path)) found.push('smsBody');
  return found;
};

// Maps the server token report to per-surface, per-tier issue messages (with
// tone) for the tokens each surface currently carries. Feeds the live per-field
// text; the send gate reads its keys. missingFallback uses the no-fallback
// matcher so a defaulted {path|…} (incl. the empty {path|}) is accepted.
// knownKeys (the full variable catalog) splits unknowns: a catalogued key is a
// real variable that just needs its context added; anything else is misspelled.
// Undefined (catalog still loading) keeps the add-a-context guidance for all.
export const buildFieldTokenMessages = (
  report: IAdHocTokenReport | undefined,
  values: IAuthoredContentValues,
  sessionTerm: string,
  knownKeys?: Set<string>
): Partial<Record<InsertTarget, IFieldTokenMessage[]>> => {
  if (!report) return {};

  const contextGated = knownKeys ? report.unknown.filter((key) => knownKeys.has(key)) : report.unknown;
  const misspelled = knownKeys ? report.unknown.filter((key) => !knownKeys.has(key)) : [];

  const tierDefs: {
    keys: string[];
    template: string;
    tone: TokenIssueTone;
    matcher: (text: string, path: string) => boolean;
  }[] = [
    { keys: contextGated, template: messages.tokenIssueUnknown.defaultMessage, tone: 'danger', matcher: tokenPresent },
    { keys: misspelled, template: messages.tokenIssueSpelling.defaultMessage, tone: 'danger', matcher: tokenPresent },
    { keys: report.missingFallback, template: messages.tokenIssueNeedsDefault.defaultMessage, tone: 'warn', matcher: tokenPresentWithoutFallback },
    { keys: report.splitTokens, template: messages.tokenIssueSplit.defaultMessage, tone: 'danger', matcher: tokenPresent }
  ];
  const tiers = tierDefs.filter((tier) => tier.keys.length > 0);
  if (!tiers.length) return {};

  // Strip the HTML body once here, not once per reported token path.
  const surfaces = { subject: values.subject, bodyText: stripHtml(values.bodyHtml), smsBody: values.smsBody };
  const byField: Partial<Record<InsertTarget, IFieldTokenMessage[]>> = {};
  tiers.forEach((tier) => {
    const keysByTarget = new Map<InsertTarget, string[]>();
    tier.keys.forEach((path) => {
      surfacesContaining(path, surfaces, tier.matcher).forEach((target) => {
        keysByTarget.set(target, [...(keysByTarget.get(target) || []), path]);
      });
    });
    keysByTarget.forEach((keys, target) => {
      const text = fillMessage(tier.template, buildTokenIssueValues(keys, sessionTerm));
      byField[target] = [...(byField[target] || []), { tone: tier.tone, text }];
    });
  });

  return byField;
};
